// ===========================================
// Attendance Report Page
// ===========================================
// Comprehensive attendance report with filters, summary, trends, and export

import { useState } from 'react';
import { useSchoolWeek } from '../../../hooks/useSchoolWeek';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Pagination,
  Alert,
} from '@mui/material';
import { Assessment as AssessmentIcon } from '@mui/icons-material';

import { AttendanceReportFilters } from '../../../api/reports.api';
import {
  useAttendanceReport,
  useExportAttendanceReport,
} from '../../../hooks/useReports';
import PageHeader from '../../../components/common/PageHeader';
import AttendanceFilters from '../../../components/reports/AttendanceFilters';
import AttendanceSummaryCards from '../../../components/reports/AttendanceSummaryCards';
import AttendanceTrendChart from '../../../components/reports/AttendanceTrendChart';
import ExportButtons from '../../../components/reports/ExportButtons';

// ===========================================
// COMPONENT
// ===========================================

export default function AttendanceReportPage() {
  // The page opens on the SCHOOL's current week (Monday–Sunday) rather than a
  // "select filters" card — an empty card reads as "no data". The heading says
  // which week, leading with the term week when one applies.
  const { week, describe } = useSchoolWeek();
  const [filters, setFilters] = useState<AttendanceReportFilters>(() => ({ ...week }));
  const [appliedFilters, setAppliedFilters] = useState<AttendanceReportFilters>(() => ({ ...week }));
  // Remounts the filter card on Reset so its date pickers show the week again.
  const [filtersKey, setFiltersKey] = useState(0);

  // Pagination state
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Fetch report data (only when filters are applied)
  const { data: reportData, isLoading, isError, refetch } = useAttendanceReport(appliedFilters);
  const exportMutation = useExportAttendanceReport();

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPage(1); // Reset to first page
  };

  const handleResetFilters = () => {
    setFilters({ ...week });
    setAppliedFilters({ ...week });
    setFiltersKey((k) => k + 1);
    setPage(1);
  };

  const handleExport = (format: 'csv' | 'pdf') => {
    exportMutation.mutate({ format, filters: appliedFilters });
  };

  // Pagination logic
  const breakdown = reportData?.breakdown || [];
  const paginatedBreakdown = breakdown.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );
  const pageCount = Math.ceil(breakdown.length / rowsPerPage);

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="Attendance Report"
        subtitle={describe(appliedFilters) ?? "Comprehensive attendance analysis and trends"}
        icon={<AssessmentIcon />}
        action={
          reportData && (
            <ExportButtons
              onExport={handleExport}
              loading={exportMutation.isPending}
              disabled={!reportData}
            />
          )
        }
      />

      {/* Filters */}
      <AttendanceFilters
        key={filtersKey}
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State — never an empty table: an error is not "no data" */}
      {!isLoading && isError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The attendance report could not be loaded. Nothing below reflects this period.
        </Alert>
      )}

      {/* Report Content */}
      {!isLoading && reportData && (
        <>
          {/* Summary Cards */}
          <AttendanceSummaryCards summary={reportData.summary} />

          {/* Trend Chart */}
          {reportData.trends && reportData.trends.length > 0 && (
            <AttendanceTrendChart trends={reportData.trends} />
          )}

          {/* Breakdown Table */}
          {breakdown.length > 0 ? (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Breakdown by Class
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Class Name</TableCell>
                        <TableCell>Teacher</TableCell>
                        <TableCell align="center">Students</TableCell>
                        <TableCell align="center">Total Lessons</TableCell>
                        <TableCell align="center">Present</TableCell>
                        <TableCell align="center">Absent</TableCell>
                        <TableCell align="center">Excused</TableCell>
                        <TableCell align="right">Attendance Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedBreakdown.map((record) => (
                        <TableRow key={record.lessonId}>
                          <TableCell>{record.lessonName}</TableCell>
                          <TableCell>{record.teacherName}</TableCell>
                          <TableCell align="center">{record.studentCount}</TableCell>
                          <TableCell align="center">{record.totalLessons}</TableCell>
                          <TableCell align="center">{record.presentCount}</TableCell>
                          <TableCell align="center">{record.absentCount}</TableCell>
                          <TableCell align="center">{record.excusedCount}</TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                color:
                                  record.attendanceRate >= 80
                                    ? 'success.main'
                                    : record.attendanceRate >= 60
                                      ? 'warning.main'
                                      : 'error.main',
                              }}
                            >
                              {record.attendanceRate.toFixed(1)}%
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {pageCount > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={(_, value) => setPage(value)}
                      color="primary"
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No attendance data found for the selected filters
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
